import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { api } from '../utils/api';
import { PageHeader, StatCard, Panel, StatusPill, Empty, Skeleton, Icon, useToast, SortTh, useSort, Pager, usePager } from '../../components/ui';

const TABS = [['all', 'All posts'], ['published', 'Published'], ['draft', 'Drafts'], ['failed', 'Failed']];

const POST_TIMEOUT = 90;  // seconds, text post only (no Reel)
const REEL_TIMEOUT = 150; // seconds, Reel generation with FFmpeg

const MONO = { fontFamily: 'ui-monospace, "SF Mono", Menlo, Consolas, monospace', fontSize: 12.5 };

// ── Step status pills ─────────────────────────────────────────────────────────
const STEP_PILL = {
  active: ['running', 'In progress'],
  done: ['completed', 'Done'],
  failed: ['failed', 'Failed'],
  skipped: ['skipped', 'Skipped'],
};

function StepPill({ status }) {
  if (!STEP_PILL[status]) return <span className="ops-pill ops-pill--neutral">Pending</span>;
  const [s, label] = STEP_PILL[status];
  return <StatusPill status={s} label={label} />;
}

function AuditHint({ msg }) {
  return (
    <p className="ops-help" style={{ margin: 0, lineHeight: 1.6 }}>
      {msg && <><span style={{ color: 'var(--red)' }}>{msg}</span><br /></>}
      Check the <Link to="/marketing/audit" className="ops-link">Audit Log</Link> for details.
    </p>
  );
}

// One panel for both post and Reel generation: a progress bar and step list while
// running, then whatever the caller renders for the outcome.
function GenerationPanel({ title, steps, statuses, pct, remaining, result, hasFail, children }) {
  const pill = result === 'success' ? <StatusPill status="completed" label="Done" />
    : result === 'failed' ? <StatusPill status="failed" />
      : result === 'timeout' ? <StatusPill status="incomplete" label="Still working" />
        : <span className="ops-help">about {remaining}s left</span>;
  return (
    <Panel title={title} action={pill}>
      {result ? children : (
        <>
          <div className="ops-progress" style={{ maxWidth: 'none', margin: '0 0 10px' }}>
            <div className="ops-progress__bar" style={{ width: `${pct}%`, background: hasFail ? 'var(--red)' : undefined }} />
          </div>
          <ul className="ops-list">
            {steps.map(s => (
              <li key={s.id} style={{ padding: '8px 0' }}>
                <span style={{ color: statuses[s.id] === 'pending' ? 'var(--ink-3)' : 'var(--ink)' }}>{s.label}</span>
                <StepPill status={statuses[s.id]} />
              </li>
            ))}
          </ul>
        </>
      )}
    </Panel>
  );
}

// ── Shared audit-log poller hook ──────────────────────────────────────────────
function useAuditPoller(triggerTime, active) {
  const [audit, setAudit] = useState({});
  const [failMsg, setFailMsg] = useState('');

  useEffect(() => {
    if (!active || !triggerTime) return undefined;
    const id = setInterval(async () => {
      try {
        const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Detroit' });
        const r = await api.getAuditLog(`?date=${today}`);
        const recent = (r.items || []).filter(e => e.timestamp >= triggerTime);
        const steps = {};
        recent.forEach(e => {
          steps[e.action_type] = e.success ? 'done' : 'failed';
          if (!e.success && e.error_message) setFailMsg(e.error_message);
        });
        setAudit(steps);
      } catch { /* ignore */ }
    }, 6000);
    return () => clearInterval(id);
  }, [triggerTime, active]);

  return { audit, failMsg };
}

// ── Post generation ───────────────────────────────────────────────────────────
const POST_STEPS = [
  { id: 'brand_voice', label: 'Reading brand voice' },
  { id: 'drafting', label: 'Writing post with AI' },
  { id: 'safety', label: 'Safety check' },
  { id: 'publishing', label: 'Publishing to Facebook' },
];

function postStepStatuses(elapsed, audit) {
  const done = t => audit[t] === 'done';
  const failed = t => audit[t] === 'failed';
  return {
    brand_voice: elapsed > 4 ? 'done' : elapsed > 0 ? 'active' : 'pending',
    drafting: done('POST_DRAFTED') ? 'done'
      : failed('POST_FAILED') ? 'failed'
        : elapsed > 4 ? 'active' : 'pending',
    safety: failed('SAFETY_BLOCK') ? 'failed'
      : done('POST_PUBLISHED') ? 'done'
        : done('POST_DRAFTED') ? 'active' : 'pending',
    publishing: done('POST_PUBLISHED') ? 'done'
      : failed('POST_FAILED') && !failed('SAFETY_BLOCK') ? 'failed'
        : done('POST_DRAFTED') ? 'active' : 'pending',
  };
}

function PostGenerationProgress({ knownIds, triggerTime, onNewPost, onTimeout }) {
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const [newPost, setNewPost] = useState(null);
  const { audit, failMsg } = useAuditPoller(triggerTime, !result);

  useEffect(() => {
    const start = Date.now();
    let done = false;
    const ticker = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);

    const postPoller = setInterval(async () => {
      if (done) return;
      try {
        const r = await api.getPosts();
        const found = (r.items || []).find(p => !knownIds.has(p.post_id));
        if (found) { finish(); setNewPost(found); setResult('success'); setTimeout(() => onNewPost(found), 1400); }
      } catch { /* ignore */ }
    }, 8000);

    const deadline = setTimeout(() => { finish(); setResult('timeout'); onTimeout(); }, POST_TIMEOUT * 1000);

    function finish() {
      if (done) return; done = true;
      clearInterval(ticker); clearInterval(postPoller); clearTimeout(deadline);
    }
    return finish;
  }, [knownIds, triggerTime, onNewPost, onTimeout]);

  // Detect hard failure from the audit log
  useEffect(() => {
    if (!result && (audit.POST_FAILED === 'failed' || audit.SAFETY_BLOCK === 'failed')) {
      setResult('failed');
    }
  }, [audit, result]);

  const statuses = postStepStatuses(elapsed, audit);
  const doneCount = POST_STEPS.filter(s => statuses[s.id] === 'done').length;
  const hasFail = POST_STEPS.some(s => statuses[s.id] === 'failed');
  const pct = result === 'success' ? 100
    : hasFail ? Math.min(doneCount / POST_STEPS.length * 100, 80)
      : Math.min(Math.max(doneCount / POST_STEPS.length * 100, elapsed / POST_TIMEOUT * 85), 92);

  const title = result === 'success' ? 'Post published to Facebook'
    : result === 'failed' ? 'Post generation failed'
      : result === 'timeout' ? 'Taking longer than expected'
        : 'Generating your Facebook post';

  return (
    <GenerationPanel title={title} steps={POST_STEPS} statuses={statuses} pct={pct}
      remaining={Math.max(0, POST_TIMEOUT - elapsed)} result={result} hasFail={hasFail}>
      {result === 'success' && newPost && (
        <>
          <div className="ops-actions" style={{ marginBottom: 10 }}>
            <StatusPill status={newPost.status} />
            {newPost.facebook_post_id && <span className="ops-help" style={MONO}>FB ID {newPost.facebook_post_id}</span>}
          </div>
          <div className="ops-note" style={{ whiteSpace: 'pre-wrap', lineHeight: 1.65 }}>{newPost.content}</div>
        </>
      )}
      {result === 'failed' && <AuditHint msg={failMsg} />}
      {result === 'timeout' && <p className="ops-help" style={{ margin: 0 }}>Check the <Link to="/marketing/audit" className="ops-link">Audit Log</Link> or refresh in a minute.</p>}
    </GenerationPanel>
  );
}

// ── Reel generation ───────────────────────────────────────────────────────────
const REEL_STEPS = [
  { id: 'reel_flyer', label: 'Generating flyer image' },
  { id: 'reel_gen', label: 'Creating video (FFmpeg)' },
  { id: 'reel_pub', label: 'Publishing to Facebook Reels' },
];

function reelStepStatuses(elapsed, audit) {
  const done = t => audit[t] === 'done';
  const failed = t => audit[t] === 'failed';
  return {
    reel_flyer: done('REEL_FLYER_GENERATED') ? 'done'
      : failed('REEL_FAILED') ? 'failed'
        : elapsed > 3 ? 'active' : 'pending',
    reel_gen: done('REEL_GENERATED') ? 'done'
      : failed('REEL_FAILED') ? 'failed'
        : done('REEL_FLYER_GENERATED') ? 'active' : 'pending',
    reel_pub: done('REEL_PUBLISHED') ? 'done'
      : failed('REEL_FAILED') ? 'failed'
        : done('REEL_GENERATED') ? 'active' : 'pending',
  };
}

function ReelGenerationProgress({ triggerTime, onDone }) {
  const [elapsed, setElapsed] = useState(0);
  const [result, setResult] = useState(null);
  const { audit, failMsg } = useAuditPoller(triggerTime, !result);

  useEffect(() => {
    const start = Date.now();
    let done = false;
    const ticker = setInterval(() => setElapsed(Math.floor((Date.now() - start) / 1000)), 500);
    const deadline = setTimeout(() => { finish(); setResult('timeout'); }, REEL_TIMEOUT * 1000);
    function finish() {
      if (done) return; done = true;
      clearInterval(ticker); clearTimeout(deadline);
    }
    return finish;
  }, []);

  // Detect completion from the audit log
  useEffect(() => {
    if (result) return;
    if (audit.REEL_PUBLISHED === 'done') {
      setResult('success');
      setTimeout(() => onDone(), 2000);
    } else if (audit.REEL_FAILED === 'failed') {
      setResult('failed');
    }
  }, [audit, result, onDone]);

  const statuses = reelStepStatuses(elapsed, audit);
  const doneCount = REEL_STEPS.filter(s => statuses[s.id] === 'done').length;
  const hasFail = REEL_STEPS.some(s => statuses[s.id] === 'failed');
  const pct = result === 'success' ? 100
    : hasFail ? Math.min(doneCount / REEL_STEPS.length * 100, 80)
      : Math.min(Math.max(doneCount / REEL_STEPS.length * 100, elapsed / REEL_TIMEOUT * 85), 92);

  const title = result === 'success' ? 'Reel published to Facebook'
    : result === 'failed' ? 'Reel generation failed'
      : result === 'timeout' ? 'Reel is still processing'
        : 'Generating Facebook Reel';

  return (
    <GenerationPanel title={title} steps={REEL_STEPS} statuses={statuses} pct={pct}
      remaining={Math.max(0, REEL_TIMEOUT - elapsed)} result={result} hasFail={hasFail}>
      {result === 'success' && <p className="ops-help" style={{ margin: 0 }}>Check the Page's Reels tab. Facebook may take a few minutes to process it.</p>}
      {result === 'failed' && <AuditHint msg={failMsg} />}
      {result === 'timeout' && <p className="ops-help" style={{ margin: 0 }}>FFmpeg can take up to 2 minutes. Check the <Link to="/marketing/audit" className="ops-link">Audit Log</Link> for status.</p>}
    </GenerationPanel>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────
export default function PostsPage() {
  const [showToast, toastNode] = useToast();
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [generatingReel, setGeneratingReel] = useState(false);
  const [expanded, setExpanded] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  const [tab, setTab] = useState('all');
  const [q, setQ] = useState('');
  const { sort, onSort, apply } = useSort('date', 'desc');

  const knownIdsRef = useRef(new Set());
  const triggerTimeRef = useRef('');
  const reelTriggerRef = useRef('');

  const load = useCallback(async () => {
    try {
      const r = await api.getPosts();
      const items = r.items || [];
      setPosts(items);
      setLastUpdated(new Date());
      return items;
    } catch {
      showToast('Could not load posts', 'error');
      return [];
    } finally {
      setLoading(false);
    }
  }, [showToast]);

  useEffect(() => { load(); }, [load]);

  const handleGenerate = async () => {
    const current = await load();
    knownIdsRef.current = new Set(current.map(p => p.post_id));
    triggerTimeRef.current = new Date(Date.now() - 5000).toISOString();
    try {
      await api.triggerPost();
      setGenerating(true);
    } catch (e) {
      showToast(e.message || 'Could not start post generation', 'error');
    }
  };

  const handleGenerateReel = async () => {
    reelTriggerRef.current = new Date(Date.now() - 5000).toISOString();
    try {
      await api.triggerReel();
      setGeneratingReel(true);
    } catch (e) {
      showToast(e.message || 'Could not start Reel generation', 'error');
    }
  };

  const handleNewPost = useCallback(async (post) => {
    setGenerating(false);
    await load();
    setExpanded(post.post_id);
  }, [load]);

  const handlePostTimeout = useCallback(() => { setGenerating(false); load(); }, [load]);
  const handleReelDone = useCallback(() => { setGeneratingReel(false); }, []);

  const counts = useMemo(() => posts.reduce((acc, p) => {
    acc.all += 1;
    acc[p.status] = (acc[p.status] || 0) + 1;
    return acc;
  }, { all: 0, published: 0, draft: 0, failed: 0 }), [posts]);

  const filtered = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return posts.filter(p => (tab === 'all' || p.status === tab)
      && (!needle || [p.content, p.source, p.facebook_post_id].filter(Boolean).join(' ').toLowerCase().includes(needle)));
  }, [posts, tab, q]);
  const sorted = apply(filtered, (p, col) => p[col] || '');
  const { pageRows, pagerProps } = usePager(sorted, [tab, q, sort.col, sort.dir]);

  const busy = generating || generatingReel;

  return (
    <>
      <PageHeader
        title="Posts"
        subtitle="Posts the AI agent has written and published to the Facebook Page."
        meta={lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : null}
        actions={(
          <>
            <button type="button" className="ops-btn" onClick={load} title="Refresh"><Icon name="refresh" /> Refresh</button>
            <button type="button" className="ops-btn" onClick={handleGenerateReel} disabled={busy || posts.length === 0}
              title={posts.length === 0 ? 'Generate a text post first' : 'Create a Reel from the most recent post'}>
              <Icon name="video" /> {generatingReel ? 'Creating Reel...' : 'Generate Reel'}
            </button>
            <button type="button" className="ops-btn ops-btn--primary" onClick={handleGenerate} disabled={busy}>
              <Icon name="plus" /> {generating ? 'Generating...' : 'Generate post'}
            </button>
          </>
        )}
      />

      {generating && (
        <PostGenerationProgress
          knownIds={knownIdsRef.current}
          triggerTime={triggerTimeRef.current}
          onNewPost={handleNewPost}
          onTimeout={handlePostTimeout}
        />
      )}

      {generatingReel && (
        <ReelGenerationProgress triggerTime={reelTriggerRef.current} onDone={handleReelDone} />
      )}

      {!loading && posts.length > 0 && (
        <div className="ops-kpis ops-kpis--4">
          <StatCard label="Total posts" value={counts.all} />
          <StatCard label="Published" value={counts.published} tone="green" />
          <StatCard label="Drafts" value={counts.draft} tone={counts.draft > 0 ? 'amber' : undefined} />
          <StatCard label="Failed" value={counts.failed} tone={counts.failed > 0 ? 'red' : undefined} />
        </div>
      )}

      <Panel flush
        title={(
          <div className="ops-tabs" role="tablist">
            {TABS.map(([k, l]) => (
              <button key={k} type="button" role="tab" className={tab === k ? 'is-active' : ''} onClick={() => setTab(k)}>
                {l} <span className="count">{counts[k] || 0}</span>
              </button>
            ))}
          </div>
        )}
        action={<div className="ops-search"><Icon name="search" /><input className="ops-input" value={q} onChange={e => setQ(e.target.value)} placeholder="Search posts" aria-label="Search posts" /></div>}
        foot={!loading && sorted.length > 0 ? <Pager {...pagerProps} label="posts" /> : null}>
        {loading ? <Skeleton rows={6} />
          : posts.length === 0 ? <Empty title="No posts yet">Use Generate post to have the agent write the first one.</Empty>
            : sorted.length === 0 ? <Empty title="No matching posts">Nothing matches this search or filter.</Empty> : (
              <div className="ops-table-wrap">
                <table className="ops-table">
                  <thead>
                    <tr>
                      <SortTh col="date" label="Date" sort={sort} onSort={onSort} />
                      <th>Content</th>
                      <SortTh col="status" label="Status" sort={sort} onSort={onSort} />
                      <SortTh col="source" label="Source" sort={sort} onSort={onSort} />
                      <th>Facebook post ID</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pageRows.map(p => (
                      <React.Fragment key={p.post_id}>
                        <tr className={`is-clickable${expanded === p.post_id ? ' is-selected' : ''}`}
                          onClick={() => setExpanded(expanded === p.post_id ? null : p.post_id)}>
                          <td className="mono muted">{p.date}</td>
                          <td style={{ maxWidth: 420, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{p.content}</td>
                          <td><StatusPill status={p.status} /></td>
                          <td className="muted">{p.source}</td>
                          <td className="mono muted">{p.facebook_post_id || ''}</td>
                        </tr>
                        {expanded === p.post_id && (
                          <tr>
                            <td colSpan={5} style={{ background: 'var(--surface-2)', padding: '16px 18px' }}>
                              <p style={{ whiteSpace: 'pre-wrap', fontSize: 13.5, lineHeight: 1.7, margin: 0 }}>{p.content}</p>
                              {p.hashtags?.length > 0 && (
                                <div className="ops-actions" style={{ flexWrap: 'wrap', marginTop: 10 }}>
                                  {p.hashtags.map(h => <span key={h} className="ops-pill ops-pill--blue">{h}</span>)}
                                </div>
                              )}
                              {p.error && <p className="neg" style={{ marginTop: 10, fontSize: 12.5 }}>Error: {p.error}</p>}
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
      </Panel>
      {toastNode}
    </>
  );
}
